import {
  Avatar,
  Box,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { BookingData } from "../../../../types/view-booking";
import { BaseURL } from "../../../../../../service/Api";
import { formatPhoneForWhatsApp } from "../../../utils/phone";

type BookingUser = NonNullable<BookingData["created_by"]>;

interface AuditCardProps {
  label: string;
  dateLabel: string;
  user?: BookingUser | null;
  date?: string;
  showDate?: boolean;
  emptyMessage?: string;
  loading: boolean;
}

const getMediaUrl = (path?: string | null) => {
  if (!path) return undefined;
  if (/^https?:\/\//i.test(path)) return path;
  return `${BaseURL.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
};

const getDisplayName = (user?: BookingUser | null) => {
  if (!user) return "N/A";
  return (
    `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
    user.username ||
    user.email ||
    "N/A"
  );
};

const formatAuditDate = (value?: string) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";

  const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thur", "Fri", "Sat"];
  const formattedDate = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);

  return `${weekdays[date.getDay()]}, ${formattedDate}`;
};

const getWhatsAppUrl = (phoneNumber?: string | null) => {
  const number = formatPhoneForWhatsApp(phoneNumber);
  return number ? `https://wa.me/${number}` : null;
};

function AuditCard({
  label,
  dateLabel,
  user,
  date,
  showDate = true,
  emptyMessage,
  loading,
}: AuditCardProps) {
  const name = getDisplayName(user);
  const whatsappUrl = getWhatsAppUrl(user?.phone_number);

  return (
    <Paper
      sx={{
        p: 1.5,
        borderRadius: "12px",
        boxShadow: 0,
        bgcolor: "background.paper",
      }}
    >
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: "11px",
          color: "#94a3b8",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          mb: 1,
        }}
      >
        {label}
      </Typography>
      {!loading && !user && emptyMessage ? (
        <Typography sx={{ fontWeight: 500, fontSize: "13px", lineHeight: 1.5 }}>
          {emptyMessage}
        </Typography>
      ) : (
        <Stack direction="row" spacing={1.25} alignItems="center">
          {loading ? (
            <Skeleton variant="circular" width={36} height={36} />
          ) : (
            <Avatar
              src={getMediaUrl(user?.profile_picture_url)}
              alt={name}
              sx={{ width: 36, height: 36, fontSize: 13 }}
            >
              {name !== "N/A" ? name.charAt(0).toUpperCase() : "?"}
            </Avatar>
          )}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {loading ? (
              <>
                <Skeleton width={100} />
                <Skeleton width={145} />
              </>
            ) : (
              <>
                <Typography
                  sx={{ fontWeight: 600, fontSize: "13px", lineHeight: 1.5 }}
                  noWrap
                >
                  {name}
                </Typography>
                {showDate && (
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: "11px",
                      color: "#cbd5e1",
                      lineHeight: 1.5,
                    }}
                  >
                    {dateLabel}: {formatAuditDate(date)}
                  </Typography>
                )}
              </>
            )}
          </Box>
          {!loading && user && (
            <Tooltip
              title={
                whatsappUrl
                  ? `Message ${name} on WhatsApp`
                  : `${name} has no valid WhatsApp number`
              }
            >
              <span>
                <IconButton
                  component="a"
                  href={whatsappUrl || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Message ${name} on WhatsApp`}
                  size="small"
                  disabled={!whatsappUrl}
                  sx={{
                    color: "#25D366",
                    "&.Mui-disabled": { color: "action.disabled" },
                  }}
                >
                  <WhatsAppIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      )}
    </Paper>
  );
}

interface BookingAuditCardsProps {
  bookingData?: BookingData | null;
}

export default function BookingAuditCards({
  bookingData,
}: BookingAuditCardsProps) {
  return (
    <Stack spacing={1}>
      <AuditCard
        label="CREATED BY"
        dateLabel="Created on"
        user={bookingData?.created_by}
        date={bookingData?.created_at}
        loading={!bookingData}
      />
      <AuditCard
        label="LAST UPDATED BY"
        dateLabel="Last updated on"
        user={bookingData?.last_updated_by}
        date={bookingData?.updated_at}
        showDate={Boolean(bookingData?.last_updated_by)}
        emptyMessage="This booking hasn't been updated yet."
        loading={!bookingData}
      />
    </Stack>
  );
}
